/**
 * Permission catalog (docs/01-architectuur/10-beveiliging.md, Permissiecatalogus; CO-0068).
 * A permission is module:action. Every package declares its permissions with definePermissions();
 * platform merges them into the catalog and the seed writes it to the database at every deploy.
 */
export interface PermissionDef {
  key: string;
  module: string;
  action: string;
  description: string;
}

export type PermissionSet = readonly PermissionDef[] & { module: string };

export function definePermissions<const A extends Record<string, string>>(module: string, actions: A) {
  const list = Object.entries(actions).map(([action, description]) => ({
    key: `${module}:${action}`,
    module,
    action,
    description,
  }));
  const set = Object.assign(list, { module }) as PermissionSet;
  const keys = Object.fromEntries(list.map((p) => [p.action, p.key])) as { [K in keyof A]: `${string}:${K & string}` };
  return Object.assign(set, { keys });
}

/** Pseudo-permission for actions every signed-in caller may use (their own profile, own tokens). */
export const AUTHENTICATED = 'authenticated';

export const adminPermissions = definePermissions('admin', {
  read: 'View users, roles, grants, tokens, audit log, call log, jobs and settings',
  create: 'Add users and agent identities, roles, grants, tokens and choice lists',
  update: 'Change users, roles, role permissions, grants, choice lists and settings; retry or cancel jobs',
  delete: 'Deactivate users, delete roles and grants, revoke tokens of others',
  export: 'Export the audit log and call log',
});

/** Actions that a reader grant allows on an object (docs/01-architectuur/10-beveiliging.md, Objectscope). */
export const readerActions = new Set(['read', 'export']);

export const actionOf = (permission: string) => permission.split(':')[1] ?? '';

/** The catalog of all packages; apps/server passes the permission sets of every package it composes. */
export function buildCatalog(sets: readonly (readonly PermissionDef[])[]): PermissionDef[] {
  const map = new Map<string, PermissionDef>();
  for (const s of sets) for (const p of s) map.set(p.key, p);
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export interface SeedRole {
  key: string;
  name: string;
  description: string;
  isSystem: boolean;
  tenantWideScope: boolean;
  /** Which catalog permissions the role gets at the first seed. */
  grants: (p: PermissionDef) => boolean;
}

/**
 * Seed roles (ADR-0013, CO-0008). admin is fixed and restored at every deploy; the others are only created
 * at the first seed and are normal, editable data afterwards.
 */
export const seedRoles: SeedRole[] = [
  {
    key: 'admin',
    name: 'Admin',
    description: 'All permissions. Fixed role; cannot be changed or deleted.',
    isSystem: true,
    tenantWideScope: true,
    grants: () => true,
  },
  {
    key: 'owner',
    name: 'Owner',
    description: 'Runs the company: everything in the domains, including approvals and exports, tenant wide.',
    isSystem: false,
    tenantWideScope: true,
    grants: (p) => p.module !== 'admin' || p.action === 'read',
  },
  {
    key: 'employee',
    name: 'Employee',
    description: 'Day-to-day work on the objects granted to them: read, create and update; no approvals.',
    isSystem: false,
    tenantWideScope: false,
    grants: (p) => (p.module !== 'admin' && ['read', 'create', 'update', 'export'].includes(p.action)) || p.key === 'agents:chat',
  },
  {
    key: 'agent',
    name: 'Agent',
    description: 'Agent identities: read, create and update within their scope. Never approve.',
    isSystem: false,
    tenantWideScope: false,
    grants: (p) => p.module !== 'admin' && ['read', 'create', 'update'].includes(p.action),
  },
  {
    key: 'worker',
    name: 'Worker',
    description: 'Worker daemons that run Claude Code and CLI agents (CO-1256): read agents and run their sessions. Never approve.',
    isSystem: false,
    tenantWideScope: false,
    grants: (p) => p.key === 'agents:read' || p.key === 'agents:work',
  },
  {
    key: 'client',
    name: 'Client',
    description: 'Client portal users (ADR-0028): read what is shared with their organisation, and submit tickets and decisions.',
    isSystem: false,
    tenantWideScope: false,
    grants: (p) => p.module === 'portal' && (p.action === 'read' || p.action === 'submit'),
  },
];
